import { computed, observable } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import { CloseButton } from "../Buttons/CloseButton";
import { SaveButton } from "../Buttons/SaveButton";
import { Area } from "../General/Area";
import { AreaBody } from "../General/AreaBody";
import { ContentGroup } from "../General/ContentGroup";
import { LanguagePicker } from "../General/LanguagePicker";
import { Toolbar } from "../General/Toolbar";
import { InputTextComponent } from "../Inputs/InputTextComponent";
import * as SettingsManagement from "../SettingsManagement";
import { LanguageInfo, InputText } from "../Shared";
import * as UiStore from "../States/AreaUiInfo";
import { AppStore } from "../Stores/AppStore";
import { DialogResult, DialogStore } from "../Stores/DialogStore";
import { NotificationStore } from "../Stores/NotificationStore";
import { Strings } from "../Stores/StringsStore";
import { UiRepos } from "../Stores/UiStore";
import { Urls } from "../Urls";
import * as Utilities from "../Utilities";
import { FileUpload } from "./BrandingEdit.FileUpload";
import { ColorPicker, Callout, DirectionalHint, PrimaryButton, DefaultButton, Stack } from 'office-ui-fabric-react';
import { BrandingDetail } from "../SettingsManagement";
import { Button } from "../Buttons/Button";
import { IColor } from "office-ui-fabric-react/lib/utilities/color/interfaces";


@observer
export class BrandingEdit extends React.Component<{}, {}> {
  @observable model: SettingsManagement.BrandingDetailsGroup[] = [];
  @observable hash: string;
  @observable className: string = "content-additional";
  @observable selectedLanguage: string;
  areaUiInfo: UiStore.AreaUiInfoImpl = new UiStore.AreaUiInfoImpl(UiRepos.BRANDING_UI_INFO);

  componentDidMount() {
    let uiLang = localStorage.getItem("brandingSelectedLanguage");

    if (!uiLang) {
      uiLang = Utilities.getUiLanguage();
      if (!uiLang) {
        uiLang = AppStore.context.userInterfaceLanguages[0].name;
      }
      localStorage.setItem("brandingSelectedLanguage", uiLang);
    }
   this.selectedLanguage = uiLang;
    this.getModel();
    this.loadUiSettings();
    this.setTitle();
  }

  componentDidUpdate() {
    this.setTitle();
  }

  setTitle = () => {
    AppStore.setTitle(Strings.globalSettings.brandingEdit.title);
  }

  private loadUiSettings = () => {
    this.areaUiInfo.contentGroupAccordionStatus.replace(UiRepos.brandingUiInfo.contentGroupAccordionStatus);
  }

  @computed get canSave(): boolean {
    let canSave = true;
    for (let i = 0; i < this.model.length; i++) {
      let group = this.model[i];
      for (let j = 0; j < group.details.length; j++) {
        let detail = group.details[j];
        if (!detail.value.isValid) {
          canSave = false;
          break;
        }
      }
    }
    canSave = canSave && this.hash !== JSON.stringify(this.model);
    AppStore.isDirtyMode = canSave;
    AppStore.isDirtyChange = this.hash !== JSON.stringify(this.model);
    AppStore.saveOnLeave = this.saveOnDirtyLeave;
    return canSave;
  }

  get languageOptions() {
    let result: LanguageInfo[] = [];
    AppStore.context.userInterfaceLanguages
      .sort((a, b) => {
        if (a.caption < b.caption) return -1;
        if (a.caption > b.caption) return 1;
        return 0;
      })
      .forEach(lang => result.push(lang));
    return result;
  }

  onLanguageSelect = (option: LanguageInfo) => {
    if (option.name !== this.selectedLanguage) {
      if (this.canSave) {
        DialogStore.show(Strings.general.keepOnPageDialogTitle, Strings.general.keepOnPageDialogText).then((result: DialogResult) => {
          if (result === DialogResult.Yes) {
            this.saveModel(false, () => this.setLanguage(option.name));
          }
          if (result === DialogResult.No) {
            this.setLanguage(option.name);
          }
        });
      }
      else {
        this.setLanguage(option.name);
      }
    }
  }

  private setLanguage(language: string) {
    this.selectedLanguage = language;
    this.getModel();
    localStorage.setItem("brandingSelectedLanguage", language);
  }

  getModel() {
    AppStore.isBusyModelLoading = true;
    SettingsManagement.getBrandingAsync(this.selectedLanguage).then(model => {
      this.model = model;
      this.hash = JSON.stringify(this.model);
      NotificationStore.clearMessage();
      AppStore.isBusyModelLoading = false;
    }).catch(error => {
      DialogStore.showError(Strings.general.errorDialogTitle, error);
    });
  }

  handleSaveUi = () => {
    this.saveModel(true);
  }

  saveOnDirtyLeave = (onSaveCompleted: () => void) => {
    this.saveModel(false, onSaveCompleted);
  }


  private saveModel(update: boolean, onSaveCompleted?: () => void) {
    if (this.canSave) {
      AppStore.isBusy = true;
      let updates: SettingsManagement.BrandingUpdateInfo[] = [];
      for (let i = 0; i < this.model.length; i++) {
        let group = this.model[i];
        for (let j = 0; j < group.details.length; j++) {
          let detail = group.details[j];
          let item = new SettingsManagement.BrandingUpdateInfo();
          item.name = detail.name;
          item.value = detail.file ? detail.file.value : detail.value.value;
          updates.push(item);
        }
      }
      let updateModel = new SettingsManagement.BrandingUpdate();
      updateModel.language = this.selectedLanguage;
      updateModel.updates = updates;
      SettingsManagement.saveBrandingAsync(updateModel).then(model => {
        AppStore.isBusy = false;
        let invalid = false;
        for (let i = 0; i < this.model.length; i++) {
          let group = this.model[i];
          for (let j = 0; j < group.details.length; j++) {
            let detail = group.details[j];
            if (!detail.value.isValid) {
              invalid = true;
              DialogStore.showError(Strings.general.errorDialogTitle, detail.value.validationMessage);
              break;
            }
            if (invalid) {
              break;
            }
          }
        }
        if (!invalid) {
          NotificationStore.showSuccessMessage(Strings.general.saveSuccessMessage);
        }
        if (update) {
          this.model = model;
          this.hash = JSON.stringify(this.model);
        }
        Urls.reloadBranding();
        AppStore.init();
        if (onSaveCompleted) {
          onSaveCompleted();
        }
      }).catch(error => {
        AppStore.isBusy = false;
        DialogStore.showError(Strings.general.errorDialogTitle, error);
      });
    }
  }

  handleCancel = () => {
    AppStore.keepOnPage(Urls.home);
  }

  handleChange = () => {
    NotificationStore.clearMessage();
    this.forceUpdate();
  }

  getValue(name: string): string {
    for (let i = 0; i < this.model.length; i++) {
      let group = this.model[i];
      for (let j = 0; j < group.details.length; j++) {
        let detail = group.details[j];
        if (detail.name === name) {
          return detail.value.value.trim() || detail.value.placeHolder.trim() || "lime";
        }
      }
    }
    return "lime";
  }

  onScroll = () => {
    let contentDiv = document.getElementById("body-content");
    let previewDiv = document.getElementById("content-additional");
    let style = window.getComputedStyle(previewDiv);
    let top = parseInt(style.getPropertyValue("top"));
    if (!top) {
      top = 0;
    }
    let margin = parseInt(style.getPropertyValue("margin-top"));
    let padding = parseInt(style.getPropertyValue("padding-top"));
    if (contentDiv.scrollTop > previewDiv.offsetTop - top - margin - padding) {
      this.className = "content-additional is--fixed";
    } else {
      this.className = "content-additional";
    }
  }

  render() {
    let AppBorder = { fill: '#ccc' };

    let HeadBg = { fill: this.getValue('HeadBg') };
    let HeadText = { fill: this.getValue('HeadText') };
    let HeadIcon = { fill: this.getValue('HeadIcon') };

    let ToolbarBg = { fill: this.getValue('ToolbarBg') };
    let ToolbarIcon = { fill: this.getValue('ToolbarIcon') };
    let ToolbarText = { fill: this.getValue('ToolbarText'), opacity: 0.5 };

    let NavPrimaryBg = { fill: this.getValue('NavPrimaryBg') };
    let NavPrimaryText = { fill: this.getValue('NavPrimaryText') };
    let NavPrimaryBorder = { fill: this.getValue('NavPrimaryBorder') };

    let BasicBorder = { fill: this.getValue('BasicBorder') };
    let BasicHeadlines = { fill: this.getValue('BasicHeadlines') };
    let BasicText = { fill: this.getValue('BasicText'), opacity: 0.5 };
    let BasicIcon = { fill: this.getValue('BasicIcon') };

    let lang = AppStore.context.userInterfaceLanguages.filter(l => l.name === this.selectedLanguage)[0];

    return <Area>
      <AreaBody loading={AppStore.isBusyModelLoading}>
        <Toolbar>
          <SaveButton onClick={this.handleSaveUi} disabled={!this.canSave} testId="saveButton" />
          <LanguagePicker options={this.languageOptions} value={(this.selectedLanguage != null && this.selectedLanguage != undefined) ? this.selectedLanguage.split('-')[0] : null} onChange={this.onLanguageSelect} isBranding={true} />
          <span className="buttongroup is--buttongroup-right">
            <CloseButton onClick={this.handleCancel} testId="closeButton" />
          </span>
        </Toolbar>
        <div className="area-grid__body-content" onScroll={this.onScroll} id="body-content">
          <div className="content-main">
            <h1>{Strings.globalSettings.brandingEdit.title}</h1>

            {this.model.map((group, groupIndex) => {

              return <ContentGroup key={group.caption} caption={group.caption} uiContext={this.areaUiInfo} testName="contentGroup" testId={group.caption}>
                {group.details.map((item, index) => {
                  return item.file
                    ? <FileUpload key={"FileUpload-" + item.name} item={item} handleChange={this.handleChange} testName="fileUpload" testId={item.name} />
                    : <BrandingTextbox key={item.name} item={item} lang={lang} handleChange={this.handleChange} />;
                })}
              </ContentGroup>;
            })}
          </div>
          <div className={this.className} id="content-additional">
            <h2>{Strings.globalSettings.brandingEdit.previewTitle}</h2>
            <div className="branding-preview">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 768">

                <path style={AppBorder} d="M1022,2V766H2V2H1022m2-2H0V768H1024V0h0Z" />

                <rect style={HeadBg} x="2" y="2" width="1020" height="54" />
                <rect style={HeadText} x="372" y="20" width="280" height="18" />
                <rect style={HeadIcon} x="16" y="16" width="24" height="24" />

                <rect style={ToolbarBg} x="2" y="56" width="1020" height="30" />
                <rect style={ToolbarIcon} x="16" y="64" width="14" height="14" />
                <rect style={ToolbarText} x="38" y="67" width="50" height="8" />
                <rect style={ToolbarIcon} x="114" y="64" width="14" height="14" />
                <rect style={ToolbarText} x="136" y="67" width="50" height="8" />
                <rect style={ToolbarIcon} x="1000" y="64" width="14" height="14" />

                <rect style={NavPrimaryBg} x="18" y="114" width="460" height="140" />
                <path style={NavPrimaryBorder} d="M478,114V254H18V114H478m2-2H16V256H480V112h0Z" />
                <rect style={NavPrimaryText} x="34" y="130" width="32" height="32" />
                <rect style={NavPrimaryText} x="76" y="134" width="90" height="20" />

                <rect style={BasicBorder} x="16" y="320" width="464" height="2" />
                <rect style={BasicBorder} x="514" y="448" width="350" height="2" />
                <rect style={BasicHeadlines} x="514" y="112" width="222" height="12" />
                <rect style={BasicHeadlines} x="514" y="256" width="222" height="12" />
                <rect style={BasicIcon} x="16" y="344" width="14" height="14" />
                <rect style={BasicIcon} x="16" y="376" width="14" height="14" />
                <rect style={BasicIcon} x="16" y="408" width="14" height="14" />

                <rect style={BasicText} x="16" y="296" width="184" height="8" />
                <rect style={BasicText} x="40" y="347" width="348" height="8" />
                <rect style={BasicText} x="40" y="379" width="348" height="8" />
                <rect style={BasicText} x="40" y="411" width="348" height="8" />
                <rect style={BasicText} x="514" y="146" width="348" height="8" />
                <rect style={BasicText} x="514" y="166" width="348" height="8" />
                <rect style={BasicText} x="514" y="186" width="348" height="8" />
                <rect style={BasicText} x="514" y="206" width="328" height="8" />
                <rect style={BasicText} x="514" y="290" width="348" height="8" />
                <rect style={BasicText} x="514" y="310" width="348" height="8" />
                <rect style={BasicText} x="514" y="330" width="348" height="8" />
                <rect style={BasicText} x="514" y="350" width="328" height="8" />
                <rect style={BasicText} x="786" y="460" width="78" height="8" />
                <rect style={BasicText} x="694" y="460" width="78" height="8" />
              </svg>
            </div>
          </div>
        </div>
      </AreaBody>
    </Area>;
  }
}

interface IBrandingTextboxProps {
  item: BrandingDetail;
  lang: LanguageInfo;
  handleChange: () => void;
}

@observer
class BrandingTextbox extends React.Component<IBrandingTextboxProps, {}> {

  @observable isVisible = false;

  isColorTextbox(input: InputText): boolean {
    return input.placeHolder && (input.placeHolder.search("#") > -1 || input.placeHolder.search("rgba") > -1);
  }

  getColorValueFromInputText(input: InputText): string {
    return this.getColorValue(input.value, input.placeHolder);
  }

  getColorValue(value: string, placeHolder: string): string {
    return value ? value : placeHolder.trim()
  }

  onSelectColor = (color: string, value: InputText) => {
    value.value = color;
  }

  refs: {
    [key: string]: HTMLElement,
    /**
    * reference name for getting Buttons from DOM.
    */
    calloutRef: HTMLElement
  }

  render() {
    let { item, lang } = this.props;

    return <div className="branding-input">
      <InputTextComponent model={item.value}
        onChange={this.props.handleChange}
        testName='inputText'
        testId={this.props.item.name}
        languageDependent={item.languageDependent}
        languageCaption={lang && lang.caption}
        region={lang && lang.region} />
      {
        this.isColorTextbox(item.value)
          ? <>
            <div ref="calloutRef" onClick={() => this.isVisible = true} style={{ backgroundColor: this.getColorValueFromInputText(item.value) }}></div>
            {
              this.isVisible
                ? <ColorPickerCallout
                  target={this.refs.calloutRef}
                  value={item.value.value}
                  placeHolder={item.value.placeHolder}
                  onSelect={(color: string) => this.onSelectColor(color, item.value)}
                  onClose={() => this.isVisible = false}
                  getColorValue={this.getColorValue} />
                : null
            }
          </>
          : null
      }
    </div>
  }
}

interface IColorPickerProps {
  value: string;
  placeHolder: string;
  target: HTMLElement;
  getColorValue: (value: string, placeHolder: string) => string;
  onSelect: (value: string) => void;
  onClose: () => void;
}

class ColorPickerCallout extends React.Component<IColorPickerProps, {}> {

  originalColor: string = this.props.value;

  onColorChange = (ev: React.SyntheticEvent<HTMLElement>, color: IColor) => {
    this.props.onSelect(color.str);
  }

  onAcceptButtonClick = () => {
    this.props.onSelect(this.props.getColorValue(this.props.value, this.props.placeHolder));
    this.originalColor = this.props.value;
    this.props.onClose();
  }

  onCancelButtonClick = () => {
    this.props.onSelect(this.originalColor);
    this.props.onClose();
  }

  getColorPickerMargin() {
    return {
      root: [
        {
          margin: 10
        }
      ]
    }
  }

  get colorPicker(): JSX.Element {
    return <Stack gap={5} padding={10}>
      <Stack.Item>
        <Stack horizontal={true}>
          <ColorPicker color={this.props.getColorValue(this.props.value, this.props.placeHolder)}
            onChange={this.onColorChange}
            alphaSliderHidden={true}
            redLabel={Strings.globalSettings.brandingEdit.colorPickerRedLabel}
            greenLabel={Strings.globalSettings.brandingEdit.colorPickerGreenLabel}
            blueLabel={Strings.globalSettings.brandingEdit.colorPickerBlueLabel} />
        </Stack>
      </Stack.Item>
    </Stack>
  }

  get buttons(): JSX.Element {
    return <Stack horizontal={true} horizontalAlign="end" gap={5} padding={10}>
      <Stack.Item>
        <PrimaryButton
          key="select"
          onClick={this.onAcceptButtonClick}
          text={Strings.general.accept}
        />
      </Stack.Item>
      <Stack.Item>
        <DefaultButton
          key="cancel"
          onClick={this.onCancelButtonClick}
          text={Strings.general.cancel}
        />
      </Stack.Item>
    </Stack>
  }

  render() {
    return (
      <Callout
        onDismiss={this.onCancelButtonClick}
        target={this.props.target}
        setInitialFocus={true}
        directionalHint={DirectionalHint.leftCenter}>
        {this.colorPicker}
        {this.buttons}
      </Callout>
    )
  }
}